import Papa from 'papaparse';
import { format } from 'date-fns';
import { GetListContactsForExport } from 'api/canmail/CANmailAPI';
import { downloadCsv } from 'utils/downloadCsv';

/**
 * CSV column order. Mirrors the bulk-upload template first (so an exported file is
 * re-importable by Bulk Import, which ignores extra columns), then appends `company`.
 * Each entry is [csvHeader, contactField].
 */
const COLUMNS: ReadonlyArray<readonly [string, string]> = [
  ['firstname', 'firstName'],
  ['lastname', 'lastName'],
  ['workemail', 'workEmail'],
  ['personalemail', 'personalEmail'],
  ['primaryphone', 'primaryPhone'],
  ['secondaryphone', 'secondaryPhone'],
  ['notes', 'notes'],
  ['company', 'company'],
];

interface ExportContact {
  [key: string]: string | null | undefined;
}

interface MailListLike {
  list_id: number | string;
  list_name?: string;
}

/** Build a filesystem-safe base name from the list name. */
function safeName(listName: string | undefined): string {
  const cleaned = (listName || 'mailing-list').replace(/[^a-zA-Z0-9-_]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned || 'mailing-list';
}

/**
 * Fetch a mailing list's contacts and download them as a CSV.
 *
 * Returns the number of contacts exported. Returns 0 (and downloads nothing) when
 * the list has no contacts, so the caller can notify appropriately. Throws if the
 * fetch fails (the API wrapper rejects on a non-success response).
 */
export async function exportMailListToCsv(list: MailListLike): Promise<number> {
  const result = await GetListContactsForExport(list.list_id);
  const contacts: ExportContact[] = result?.contacts ?? [];

  if (contacts.length === 0) {
    return 0;
  }

  const fields = COLUMNS.map(([header]) => header);
  const data = contacts.map((contact) => COLUMNS.map(([, key]) => contact[key] ?? ''));
  const csv = Papa.unparse({ fields, data });

  const filename = `${safeName(list.list_name)}-contacts-${format(new Date(), 'yyyy-MM-dd')}.csv`;
  downloadCsv(filename, csv);

  return contacts.length;
}

export default exportMailListToCsv;
